import React from 'react';
import moment from 'moment';
import {
  ALPHA_NUMERIC_CHAR,
  ALPHA_NUMERIC_CHAR_LEN,
  COUNTRY_CODES,
  LOCALES,
} from '../constants/appConstants';

/**
 * Deep freeze function to prevent any edits to constants
 * at runtime
 */
export const deepFreeze = (obj) => {
  Object.keys(obj).forEach((name) => {
    const prop = obj[name];
    if (typeof prop === 'object' && prop !== null) {
      deepFreeze(prop);
    }
  });
  return Object.freeze(obj);
};

/**
 * Formats date from ISO format (YYYY-MM-DD) to DD-MM-YYYY
 *
 * @param {string} dateAndTime
 * @returns {string|null}
 */
export const formatDate = (dateAndTime) => {
  if (!dateAndTime) return null;
  const date = moment(dateAndTime, moment.ISO_8601, true);
  return date.isValid() ? date.format('DD-MM-YYYY') : dateAndTime.split('T')[0];
};

/**
 * Creates React elements from multiple rows
 *
 * @param {...any} rows
 * @returns {React.ReactElement|string|null}
 */
export const valueGetter = (...rows) => {
  const parts = rows.filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];

  return React.createElement(
    React.Fragment,
    {},
    ...parts.map((part) => React.createElement('div', {}, part))
  );
};

/**
 * Country to locale mapping
 */
const COUNTRY_LOCALE_MAP = Object.freeze({
  [COUNTRY_CODES.GB]: LOCALES.EN_GB,
  [COUNTRY_CODES.IE]: LOCALES.EN_IE,
  [COUNTRY_CODES.DE]: LOCALES.DE_DE,
});

/**
 * Returns locale based on country code
 *
 * @param {string} countryCode
 * @returns {string}
 */
export const getLocaleByCountryCode = (countryCode) => {
  if (!countryCode) {
    return LOCALES.EN_GB;
  }

  const normalizedCountryCode = countryCode.trim().toUpperCase();

  return (
    COUNTRY_LOCALE_MAP[normalizedCountryCode]
    || LOCALES.EN_GB
  );
};

/**
 * Returns realtime timestamp in milliseconds
 *
 * Example:
 * 1747839505123
 *
 * @returns {number}
 */
export const getRealtimeTimestamp = () => moment().valueOf();

/**
 * Generates random alphanumeric string
 *
 * Example:
 * 4bae8f88cd5eb167bd7fc6dfaaf690a2
 *
 * @param {number} length
 * @returns {string}
 */

export const generateSecureRandomAlphaNumeric = (
  length = ALPHA_NUMERIC_CHAR_LEN
) => {
  const characters = ALPHA_NUMERIC_CHAR;

  const randomValues = new Uint32Array(length);

  window.crypto.getRandomValues(randomValues);

  return Array.from(randomValues, (value) => characters[value % characters.length]).join('');
};
